import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { siteUrl, setConfiguredSiteUrl } from '../lib/siteUrl';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface UserAccount {
  id: string;
  name: string;
  email: string;
  accountNumber: string;
  balance: number;
  currency: string;
  verified: boolean;
  status: string;
  joinDate: string;
  password?: string;
  hidden?: boolean;
}

export interface PendingRegistration {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  zipCode: string;
  currency: string;
  documentType: string;
  documentFrontUrl: string;
  documentBackUrl: string;
  submittedDate: string;
  status: string;
  documentStatus?: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
  password?: string;
}

export interface DepositRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  method: string;
  cryptoCoin?: string;
  txHash?: string;
  status: 'pending' | 'approved' | 'rejected';
  date: string;
  rejectionReason?: string;
  adminNote?: string;
}

export interface Withdrawal {
  id: string;
  userId: string;
  userName: string;
  amount: number;
  currency: string;
  method: string;
  accountDetails: string;
  status: string;
  date: string;
  rejectionReason?: string;
  paymentMethod?: string;
  bankName?: string;
  accountNumber?: string;
  accountHolderName?: string;
  bankLocation?: string;
  paypalEmail?: string;
  cryptoCoin?: string;
  walletAddress?: string;
}

export interface Notification {
  id: string;
  type: 'admin' | 'user';
  userId?: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionType: 'registration' | 'deposit' | 'withdrawal' | 'document' | 'money_added' | 'direct_message';
  relatedId?: string;
}

export interface SiteSettings {
  siteName: string;
  siteEmail: string;
  sitePhone: string;
  siteAddress: string;
  siteTagline: string;
  siteUrl: string;
  primaryColor: string;
  accentColor: string;
  logoIcon: string;
  maintenanceMode: boolean;
  announcementBanner: {
    enabled: boolean;
    message: string;
    bgColor: string;
    textColor: string;
  };
}

export interface WalletAddresses {
  bitcoin: string;
  ethereum: string;
  usdt: string;
}

export interface PaymentMethods {
  crypto: boolean;
  bankTransfer: boolean;
  paypal: boolean;
  skrill: boolean;
  wireTransfer: boolean;
}

interface AppDataContextType {
  users: UserAccount[];
  setUsers: (users: UserAccount[]) => void;
  pendingRegistrations: PendingRegistration[];
  setPendingRegistrations: (regs: PendingRegistration[]) => void;
  addPendingRegistration: (reg: PendingRegistration) => void;
  approveRegistration: (reg: PendingRegistration) => Promise<{ success: boolean; error?: string }>;
  rejectRegistration: (id: string, reason?: string) => void;
  approveDocument: (id: string) => void;
  rejectDocument: (id: string, adminNotes: string) => void;
  addMoneyToUser: (userId: string, amount: number, currency?: string) => void;
  hideUser: (userId: string, hidden: boolean) => void;
  deleteUser: (userId: string) => void;
  depositRequests: DepositRequest[];
  addDepositRequest: (req: DepositRequest) => Promise<void>;
  approveDeposit: (id: string, adminNote?: string) => void;
  rejectDeposit: (id: string, reason: string) => void;
  withdrawals: Withdrawal[];
  setWithdrawals: (w: Withdrawal[]) => void;
  approveWithdrawal: (id: string) => void;
  rejectWithdrawal: (id: string, reason: string) => void;
  addWithdrawalRequest: (withdrawal: Withdrawal) => Promise<void>;
  currentUser: UserAccount | null;
  loginUser: (email: string, password: string) => Promise<boolean>;
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  isAdmin: boolean;
  logout: () => void;
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: (type: 'admin' | 'user', userId?: string) => void;
  getUnreadCount: (type: 'admin' | 'user', userId?: string) => number;
  getUserNotifications: (userId: string) => Notification[];
  getAdminNotifications: () => Notification[];
  walletAddresses: WalletAddresses;
  setWalletAddresses: (addresses: WalletAddresses) => void;
  paymentMethods: PaymentMethods;
  setPaymentMethods: (methods: PaymentMethods) => void;
  siteSettings: SiteSettings;
  setSiteSettings: (settings: SiteSettings) => void;
  updatePassword: (userId: string, newPassword: string) => void;
  usersLoading: boolean;
}

// ── Row mappers: user_accounts ────────────────────────────────────────────────

function rowToUser(row: Record<string, unknown>): UserAccount {
  return {
    id: row.id as string,
    name: row.name as string,
    email: row.email as string,
    accountNumber: (row.account_number as string) ?? '',
    balance: Number(row.balance ?? 0),
    currency: (row.currency as string) ?? 'USD',
    verified: Boolean(row.verified),
    status: (row.status as string) ?? 'active',
    joinDate: (row.join_date as string) ?? '',
    password: row.password as string | undefined,
    hidden: Boolean(row.hidden),
  };
}

function userToRow(user: UserAccount) {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    account_number: user.accountNumber,
    balance: user.balance,
    currency: user.currency,
    verified: user.verified,
    status: user.status,
    join_date: user.joinDate,
    password: user.password ?? null,
    hidden: user.hidden ?? false,
  };
}

// ── Row mappers: pending_registrations ────────────────────────────────────────

function rowToReg(row: Record<string, unknown>): PendingRegistration {
  return {
    id: row.id as string,
    firstName: (row.first_name as string) ?? '',
    lastName: (row.last_name as string) ?? '',
    email: (row.email as string) ?? '',
    phone: (row.phone as string) ?? '',
    address: (row.address as string) ?? '',
    city: (row.city as string) ?? '',
    country: (row.country as string) ?? '',
    zipCode: (row.zip_code as string) ?? '',
    currency: (row.currency as string) ?? 'USD',
    documentType: (row.document_type as string) ?? '',
    documentFrontUrl: (row.document_front_url as string) ?? '',
    documentBackUrl: (row.document_back_url as string) ?? '',
    submittedDate: (row.submitted_date as string) ?? '',
    status: (row.status as string) ?? 'pending',
    documentStatus: (row.document_status as 'pending' | 'approved' | 'rejected') ?? 'pending',
    adminNotes: row.admin_notes as string | undefined,
    password: row.password as string | undefined,
  };
}

function regToRow(reg: PendingRegistration) {
  return {
    id: reg.id,
    first_name: reg.firstName,
    last_name: reg.lastName,
    email: reg.email,
    phone: reg.phone,
    address: reg.address,
    city: reg.city,
    country: reg.country,
    zip_code: reg.zipCode,
    currency: reg.currency,
    document_type: reg.documentType,
    document_front_url: reg.documentFrontUrl,
    document_back_url: reg.documentBackUrl,
    submitted_date: reg.submittedDate,
    status: reg.status,
    document_status: reg.documentStatus ?? 'pending',
    admin_notes: reg.adminNotes ?? null,
    password: reg.password ?? null,
  };
}

// ── Row mappers: deposit_requests ────────────────────────────────────────────

function rowToDeposit(row: Record<string, unknown>): DepositRequest {
  return {
    id: row.id as string,
    userId: (row.user_id as string) ?? '',
    userName: (row.user_name as string) ?? '',
    userEmail: (row.user_email as string) ?? '',
    amount: Number(row.amount ?? 0),
    currency: (row.currency as string) ?? 'USD',
    method: (row.method as string) ?? '',
    cryptoCoin: row.crypto_coin as string | undefined,
    txHash: row.tx_hash as string | undefined,
    status: (row.status as 'pending' | 'approved' | 'rejected') ?? 'pending',
    date: (row.date as string) ?? '',
    rejectionReason: row.rejection_reason as string | undefined,
    adminNote: row.admin_note as string | undefined,
  };
}

function depositToRow(req: DepositRequest) {
  return {
    id: req.id,
    user_id: req.userId,
    user_name: req.userName,
    user_email: req.userEmail,
    amount: req.amount,
    currency: req.currency,
    method: req.method,
    crypto_coin: req.cryptoCoin ?? null,
    tx_hash: req.txHash ?? null,
    status: req.status,
    date: req.date,
    rejection_reason: req.rejectionReason ?? null,
    admin_note: req.adminNote ?? null,
    created_at: new Date().toISOString(),
  };
}

// ── Row mappers: withdrawals ──────────────────────────────────────────────────

function rowToWithdrawal(row: Record<string, unknown>): Withdrawal {
  return {
    id: row.id as string,
    userId: (row.user_id as string) ?? '',
    userName: (row.user_name as string) ?? '',
    amount: Number(row.amount ?? 0),
    currency: (row.currency as string) ?? 'USD',
    method: (row.method as string) ?? '',
    accountDetails: (row.account_details as string) ?? '',
    status: (row.status as string) ?? 'pending',
    date: (row.date as string) ?? '',
    rejectionReason: row.rejection_reason as string | undefined,
    paymentMethod: row.payment_method as string | undefined,
    bankName: row.bank_name as string | undefined,
    accountNumber: row.account_number_detail as string | undefined,
    accountHolderName: row.account_holder_name as string | undefined,
    bankLocation: row.bank_location as string | undefined,
    paypalEmail: row.paypal_email as string | undefined,
    cryptoCoin: row.crypto_coin as string | undefined,
    walletAddress: row.wallet_address as string | undefined,
  };
}

function withdrawalToRow(w: Withdrawal) {
  return {
    id: w.id,
    user_id: w.userId,
    user_name: w.userName,
    amount: w.amount,
    currency: w.currency,
    method: w.method,
    account_details: w.accountDetails || '',
    status: w.status,
    date: w.date,
    rejection_reason: w.rejectionReason ?? null,
    payment_method: w.paymentMethod ?? null,
    bank_name: w.bankName ?? null,
    account_number_detail: w.accountNumber ?? null,
    account_holder_name: w.accountHolderName ?? null,
    bank_location: w.bankLocation ?? null,
    paypal_email: w.paypalEmail ?? null,
    crypto_coin: w.cryptoCoin ?? null,
    wallet_address: w.walletAddress ?? null,
    created_at: new Date().toISOString(),
  };
}

// ── Row mappers: notifications ────────────────────────────────────────────────

function rowToNotification(row: Record<string, unknown>): Notification {
  return {
    id: row.id as string,
    type: (row.type as 'admin' | 'user') ?? 'admin',
    userId: row.user_id as string | undefined,
    title: (row.title as string) ?? '',
    message: (row.message as string) ?? '',
    timestamp: (row.timestamp as string) ?? new Date().toISOString(),
    read: Boolean(row.read),
    actionType: (row.action_type as Notification['actionType']) ?? 'registration',
    relatedId: row.related_id as string | undefined,
  };
}

function notificationToRow(n: Notification) {
  return {
    id: n.id,
    type: n.type,
    user_id: n.userId ?? null,
    title: n.title,
    message: n.message,
    timestamp: n.timestamp,
    read: n.read,
    action_type: n.actionType,
    related_id: n.relatedId ?? null,
  };
}

// ── Row mappers: site_settings ────────────────────────────────────────────────

function rowToSiteSettings(row: Record<string, unknown>): SiteSettings {
  return {
    siteName: (row.site_name as string) ?? 'Pinnacle Payments',
    siteEmail: (row.site_email as string) ?? 'support@pinnaclepayments.com',
    sitePhone: (row.site_phone as string) ?? '',
    siteAddress: (row.site_address as string) ?? '',
    siteTagline: (row.site_tagline as string) ?? '',
    siteUrl: (row.site_url as string) ?? '',
    primaryColor: (row.primary_color as string) ?? '#d97706',
    accentColor: (row.accent_color as string) ?? '#92400e',
    logoIcon: (row.logo_icon as string) ?? 'fa-mountain',
    maintenanceMode: Boolean(row.maintenance_mode),
    announcementBanner: {
      enabled: Boolean(row.announcement_enabled),
      message: (row.announcement_message as string) ?? '',
      bgColor: (row.announcement_bg_color as string) ?? '#f59e0b',
      textColor: (row.announcement_text_color as string) ?? '#ffffff',
    },
  };
}

function siteSettingsToRow(s: SiteSettings) {
  return {
    id: 1,
    site_name: s.siteName,
    site_email: s.siteEmail,
    site_phone: s.sitePhone,
    site_address: s.siteAddress,
    site_tagline: s.siteTagline,
    site_url: s.siteUrl,
    primary_color: s.primaryColor,
    accent_color: s.accentColor,
    logo_icon: s.logoIcon,
    maintenance_mode: s.maintenanceMode,
    announcement_enabled: s.announcementBanner.enabled,
    announcement_message: s.announcementBanner.message,
    announcement_bg_color: s.announcementBanner.bgColor,
    announcement_text_color: s.announcementBanner.textColor,
    updated_at: new Date().toISOString(),
  };
}

// ── Row mappers: wallet_addresses ─────────────────────────────────────────────

function rowToWalletAddresses(row: Record<string, unknown>): WalletAddresses {
  return {
    bitcoin: (row.bitcoin as string) ?? '',
    ethereum: (row.ethereum as string) ?? '',
    usdt: (row.usdt as string) ?? '',
  };
}

function walletToRow(w: WalletAddresses) {
  return {
    id: 1,
    bitcoin: w.bitcoin,
    ethereum: w.ethereum,
    usdt: w.usdt,
    updated_at: new Date().toISOString(),
  };
}

// ── Row mappers: payment_methods ──────────────────────────────────────────────

function rowToPaymentMethods(row: Record<string, unknown>): PaymentMethods {
  return {
    crypto: Boolean(row.crypto),
    bankTransfer: Boolean(row.bank_transfer),
    paypal: Boolean(row.paypal),
    skrill: Boolean(row.skrill),
    wireTransfer: Boolean(row.wire_transfer),
  };
}

function paymentMethodsToRow(p: PaymentMethods) {
  return {
    id: 1,
    crypto: p.crypto,
    bank_transfer: p.bankTransfer,
    paypal: p.paypal,
    skrill: p.skrill,
    wire_transfer: p.wireTransfer,
    updated_at: new Date().toISOString(),
  };
}

// ── Email log helper ──────────────────────────────────────────────────────────

function writeEmailLog(
  type: string, email: string, fromEmail: string | undefined,
  fromName: string | undefined, status: 'sent' | 'failed', errorMsg?: string
) {
  const logEntry = {
    id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type,
    recipient: email,
    from_email: fromEmail || 'onboarding@resend.dev',
    from_name: fromName || 'System',
    timestamp: new Date().toISOString(),
    status,
    error: errorMsg || null,
  };
  supabase.from('email_logs').insert(logEntry)
    .then(({ error }) => { if (error) console.error('email_logs insert:', error); });
}

// ── Email notification helper ─────────────────────────────────────────────────

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

async function sendEmailNotification(
  type: string, email: string, data: Record<string, unknown>,
  fromEmail?: string, fromName?: string
) {
  if (!SUPABASE_URL) return;
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, email, data, fromEmail, fromName }),
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      writeEmailLog(type, email, fromEmail, fromName, 'failed', result.error || 'Unknown error');
    } else {
      writeEmailLog(type, email, fromEmail, fromName, 'sent');
    }
  } catch (error: unknown) {
    writeEmailLog(type, email, fromEmail, fromName, 'failed', (error as Error)?.message || 'Network error');
  }
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteName: 'Pinnacle Payments',
  siteEmail: 'support@pinnaclepayments.com',
  sitePhone: '+1 (415) 555-1234',
  siteAddress: '789 Summit Avenue, San Francisco, CA 94102, USA',
  siteTagline: 'Elevate Your Financial Future',
  siteUrl: '',
  primaryColor: '#d97706',
  accentColor: '#92400e',
  logoIcon: 'fa-mountain',
  maintenanceMode: false,
  announcementBanner: { enabled: false, message: '', bgColor: '#f59e0b', textColor: '#ffffff' },
};

const DEFAULT_WALLETS: WalletAddresses = {
  bitcoin: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
  ethereum: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb',
  usdt: 'TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE',
};

const DEFAULT_PAYMENT_METHODS: PaymentMethods = {
  crypto: true, bankTransfer: true, paypal: true, skrill: false, wireTransfer: false,
};

// ── Brand color injection ─────────────────────────────────────────────────────

function applyColorsToDom(settings: SiteSettings) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', settings.primaryColor);
  root.style.setProperty('--color-accent', settings.accentColor);
  let styleEl = document.getElementById('pinnacle-brand-styles') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'pinnacle-brand-styles';
    document.head.appendChild(styleEl);
  }
  const p = settings.primaryColor;
  const a = settings.accentColor;
  const darken = (hex: string, amount = 20): string => {
    const n = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, (n >> 16) - amount);
    const g = Math.max(0, ((n >> 8) & 0xff) - amount);
    const b = Math.max(0, (n & 0xff) - amount);
    return `rgb(${r},${g},${b})`;
  };
  const aDark = darken(a);
  styleEl.textContent = `
    .from-amber-500{--tw-gradient-from:${p}!important}.to-amber-500{--tw-gradient-to:${p}!important}
    .from-amber-600{--tw-gradient-from:${a}!important}.to-amber-600{--tw-gradient-to:${a}!important}
    .from-amber-700{--tw-gradient-from:${aDark}!important}.to-amber-700{--tw-gradient-to:${aDark}!important}
    .bg-amber-500{background-color:${p}!important}.bg-amber-600{background-color:${a}!important}
    .hover\\:bg-amber-600:hover{background-color:${a}!important}.hover\\:bg-amber-700:hover{background-color:${aDark}!important}
    .text-amber-600{color:${p}!important}.border-amber-600{border-color:${p}!important}
    .border-amber-500{border-color:${p}!important}.focus\\:ring-amber-500:focus{--tw-ring-color:${p}66!important}
    .focus\\:border-amber-400:focus{border-color:${p}!important}
    .hover\\:text-amber-600:hover{color:${p}!important}
    .bg-amber-50{background-color:color-mix(in srgb,${p} 8%,white)!important}
    .bg-amber-100{background-color:color-mix(in srgb,${p} 14%,white)!important}
    .hover\\:bg-amber-50:hover{background-color:color-mix(in srgb,${p} 8%,white)!important}
    .hover\\:bg-amber-100:hover{background-color:color-mix(in srgb,${p} 14%,white)!important}
    .text-amber-700{color:${a}!important}.text-amber-800{color:${aDark}!important}.text-amber-900{color:${aDark}!important}
    .border-amber-200{border-color:${p}55!important}.border-amber-300{border-color:${p}88!important}
    .bg-amber-400{background-color:${p}!important}
  `;
}

// ── Session helpers (localStorage for admin auth state only) ─────────────────

const SESSION_KEY = 'pinnacle_session';

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveSession(data: object) {
  try {
    const current = loadSession() ?? {};
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, ...data }));
  } catch { /* ignore */ }
}

// ── Context ───────────────────────────────────────────────────────────────────

const AppDataContext = createContext<AppDataContextType | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const session = loadSession();

  // Business data — all sourced from Supabase, start empty
  const [users, setUsersState] = useState<UserAccount[]>([]);
  const [pendingRegistrations, setPendingRegistrationsState] = useState<PendingRegistration[]>([]);
  const [depositRequests, setDepositRequestsState] = useState<DepositRequest[]>([]);
  const [withdrawals, setWithdrawalsState] = useState<Withdrawal[]>([]);
  const [notifications, setNotificationsState] = useState<Notification[]>([]);

  // Config — from Supabase, with defaults while loading
  const [siteSettings, setSiteSettingsState] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [walletAddresses, setWalletAddressesState] = useState<WalletAddresses>(DEFAULT_WALLETS);
  const [paymentMethods, setPaymentMethodsState] = useState<PaymentMethods>(DEFAULT_PAYMENT_METHODS);

  // Session — currentUser from Supabase Auth, isAdmin from localStorage
  const [currentUser, setCurrentUser] = useState<UserAccount | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(session?.isAdmin ?? false);

  const [usersLoading, setUsersLoading] = useState(true);

  // Apply colors on mount and settings change
  useEffect(() => { applyColorsToDom(siteSettings); }, [siteSettings]);

  // ── Load ALL data from Supabase on mount ──────────────────────────────────
  useEffect(() => {
    async function fetchAll() {
      try {
        const [
          { data: usersData },
          { data: regsData },
          { data: depositsData },
          { data: withdrawalsData },
          { data: notificationsData },
          { data: settingsData },
          { data: walletData },
          { data: paymentData },
        ] = await Promise.all([
          supabase.from('user_accounts').select('*').order('created_at', { ascending: false }),
          supabase.from('pending_registrations').select('*').order('created_at', { ascending: false }),
          supabase.from('deposit_requests').select('*').order('created_at', { ascending: false }),
          supabase.from('withdrawals').select('*').order('created_at', { ascending: false }),
          supabase.from('notifications').select('*').order('created_at', { ascending: false }),
          supabase.from('site_settings').select('*').eq('id', 1).maybeSingle(),
          supabase.from('wallet_addresses').select('*').eq('id', 1).maybeSingle(),
          supabase.from('payment_methods').select('*').eq('id', 1).maybeSingle(),
        ]);

        let mapped: UserAccount[] = [];
        if (usersData) {
          mapped = usersData.map(rowToUser);
          setUsersState(mapped);
        }

        if (regsData) setPendingRegistrationsState(regsData.map(rowToReg));
        if (depositsData) setDepositRequestsState(depositsData.map(rowToDeposit));
        if (withdrawalsData) setWithdrawalsState(withdrawalsData.map(rowToWithdrawal));
        if (notificationsData) setNotificationsState(notificationsData.map(rowToNotification));

        if (settingsData) {
          const s = rowToSiteSettings(settingsData as Record<string, unknown>);
          setSiteSettingsState(s);
          applyColorsToDom(s);
          if (s.siteUrl) setConfiguredSiteUrl(s.siteUrl);
        }
        if (walletData) setWalletAddressesState(rowToWalletAddresses(walletData as Record<string, unknown>));
        if (paymentData) setPaymentMethodsState(rowToPaymentMethods(paymentData as Record<string, unknown>));

        // Restore user session from Supabase Auth (not localStorage)
        const savedSession = loadSession();
        if (!savedSession?.isAdmin) {
          const { data: { session: authSession } } = await supabase.auth.getSession();
          if (authSession?.user?.email) {
            const email = authSession.user.email.toLowerCase();
            const user = mapped.find((u) => u.email.toLowerCase() === email);
            if (user && user.status === 'active') {
              setCurrentUser(user);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load from Supabase:', err);
      } finally {
        setUsersLoading(false);
      }
    }
    fetchAll();

    // Listen for auth state changes (token expiry, sign out from other tabs, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        const s = loadSession();
        if (!s?.isAdmin) {
          setCurrentUser(null);
        }
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Supabase Realtime Subscriptions ───────────────────────────────────────
  useEffect(() => {
    // user_accounts: live balance + status updates
    const userChannel = supabase
      .channel('realtime:user_accounts')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_accounts' }, (payload) => {
        const updated = rowToUser(payload.new as Record<string, unknown>);
        setUsersState((prev) => prev.map((u) => u.id === updated.id ? updated : u));
        setCurrentUser((prev) => {
          if (prev && prev.id === updated.id) {
            // If admin suspended or deactivated this user, force sign-out immediately
            if (updated.status !== 'active') {
              supabase.auth.signOut().catch(() => {});
              saveSession({ isAdmin: false });
              return null;
            }
            return updated;
          }
          return prev;
        });
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_accounts' }, (payload) => {
        const newUser = rowToUser(payload.new as Record<string, unknown>);
        setUsersState((prev) => {
          const exists = prev.find((u) => u.id === newUser.id);
          return exists ? prev : [newUser, ...prev];
        });
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'user_accounts' }, (payload) => {
        const deletedId = (payload.old as Record<string, unknown>).id as string;
        setUsersState((prev) => prev.filter((u) => u.id !== deletedId));
        setCurrentUser((prev) => {
          if (prev && prev.id === deletedId) {
            supabase.auth.signOut().catch(() => {});
            saveSession({ isAdmin: false });
            return null;
          }
          return prev;
        });
      })
      .subscribe();

    // deposit_requests: live status updates
    const depositChannel = supabase
      .channel('realtime:deposit_requests')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'deposit_requests' }, (payload) => {
        const updated = rowToDeposit(payload.new as Record<string, unknown>);
        setDepositRequestsState((prev) => prev.map((d) => d.id === updated.id ? updated : d));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deposit_requests' }, (payload) => {
        const newDeposit = rowToDeposit(payload.new as Record<string, unknown>);
        setDepositRequestsState((prev) => {
          const exists = prev.find((d) => d.id === newDeposit.id);
          return exists ? prev : [newDeposit, ...prev];
        });
      })
      .subscribe();

    // withdrawals: live status updates
    const withdrawalChannel = supabase
      .channel('realtime:withdrawals')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'withdrawals' }, (payload) => {
        const updated = rowToWithdrawal(payload.new as Record<string, unknown>);
        setWithdrawalsState((prev) => prev.map((w) => w.id === updated.id ? updated : w));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'withdrawals' }, (payload) => {
        const newW = rowToWithdrawal(payload.new as Record<string, unknown>);
        setWithdrawalsState((prev) => {
          const exists = prev.find((w) => w.id === newW.id);
          return exists ? prev : [newW, ...prev];
        });
      })
      .subscribe();

    // notifications: live new notifications
    const notifChannel = supabase
      .channel('realtime:notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (payload) => {
        const newNotif = rowToNotification(payload.new as Record<string, unknown>);
        setNotificationsState((prev) => {
          const exists = prev.find((n) => n.id === newNotif.id);
          return exists ? prev : [newNotif, ...prev];
        });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications' }, (payload) => {
        const updated = rowToNotification(payload.new as Record<string, unknown>);
        setNotificationsState((prev) => prev.map((n) => n.id === updated.id ? updated : n));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(userChannel);
      supabase.removeChannel(depositChannel);
      supabase.removeChannel(withdrawalChannel);
      supabase.removeChannel(notifChannel);
    };
  }, []);

  // ── Setters ───────────────────────────────────────────────────────────────

  const setUsers = (u: UserAccount[]) => setUsersState(u);
  const setPendingRegistrations = (r: PendingRegistration[]) => setPendingRegistrationsState(r);
  const setWithdrawals = (w: Withdrawal[]) => setWithdrawalsState(w);

  const setWalletAddresses = (addresses: WalletAddresses) => {
    setWalletAddressesState(addresses);
    supabase.from('wallet_addresses').upsert(walletToRow(addresses))
      .then(({ error }) => { if (error) console.error('wallet_addresses upsert:', error); });
  };

  const setPaymentMethods = (methods: PaymentMethods) => {
    setPaymentMethodsState(methods);
    supabase.from('payment_methods').upsert(paymentMethodsToRow(methods))
      .then(({ error }) => { if (error) console.error('payment_methods upsert:', error); });
  };

  const setSiteSettings = (settings: SiteSettings) => {
    setSiteSettingsState(settings);
    applyColorsToDom(settings);
    if (settings.siteUrl) setConfiguredSiteUrl(settings.siteUrl);
    supabase.from('site_settings').upsert(siteSettingsToRow(settings))
      .then(({ error }) => { if (error) console.error('site_settings upsert:', error); });
  };

  // ── Notifications ─────────────────────────────────────────────────────────

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotif: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
    };
    setNotificationsState((prev) => [newNotif, ...prev]);
    supabase.from('notifications').insert(notificationToRow(newNotif))
      .then(({ error }) => { if (error) console.error('notifications insert:', error); });
  };

  const markNotificationAsRead = (id: string) => {
    setNotificationsState((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    supabase.from('notifications').update({ read: true }).eq('id', id)
      .then(({ error }) => { if (error) console.error('notifications update:', error); });
  };

  const markAllNotificationsAsRead = (type: 'admin' | 'user', userId?: string) => {
    setNotificationsState((prev) =>
      prev.map((n) => {
        if (type === 'admin' && n.type === 'admin') return { ...n, read: true };
        if (type === 'user' && n.type === 'user' && n.userId === userId) return { ...n, read: true };
        return n;
      })
    );
    if (type === 'admin') {
      supabase.from('notifications').update({ read: true }).eq('type', 'admin')
        .then(({ error }) => { if (error) console.error('notifications markAll admin:', error); });
    } else if (userId) {
      supabase.from('notifications').update({ read: true }).eq('type', 'user').eq('user_id', userId)
        .then(({ error }) => { if (error) console.error('notifications markAll user:', error); });
    }
  };

  const getUnreadCount = (type: 'admin' | 'user', userId?: string): number =>
    notifications.filter((n) => {
      if (type === 'admin') return n.type === 'admin' && !n.read;
      return n.type === 'user' && n.userId === userId && !n.read;
    }).length;

  const getUserNotifications = (userId: string): Notification[] =>
    notifications.filter((n) => n.type === 'user' && n.userId === userId);

  const getAdminNotifications = (): Notification[] =>
    notifications.filter((n) => n.type === 'admin');

  // ── Registrations ─────────────────────────────────────────────────────────

  const addPendingRegistration = (reg: PendingRegistration) => {
    const regWithStatus = { ...reg, documentStatus: 'pending' as const };
    setPendingRegistrationsState((prev) => [...prev, regWithStatus]);
    supabase.from('pending_registrations').upsert(regToRow(regWithStatus))
      .then(({ error }) => { if (error) console.error('pending_registrations insert:', error); });
    addNotification({
      type: 'admin',
      title: 'New Registration Submitted',
      message: `${reg.firstName} ${reg.lastName} has submitted a new registration application.`,
      actionType: 'registration',
      relatedId: reg.id,
    });
    // Notify admin via email
    sendEmailNotification('new_registration', siteSettings.siteEmail, {
      firstName: reg.firstName,
      lastName: reg.lastName,
      userEmail: reg.email,
      phone: reg.phone,
      country: reg.country,
      currency: reg.currency,
      documentType: reg.documentType,
      submittedDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      adminDashboardUrl: siteUrl('/admin/dashboard'),
    }, siteSettings.siteEmail, siteSettings.siteName);
  };

  const approveDocument = (id: string) => {
    setPendingRegistrationsState((prev) =>
      prev.map((r) => r.id === id ? { ...r, documentStatus: 'approved' as const } : r)
    );
    supabase.from('pending_registrations').update({ document_status: 'approved' }).eq('id', id)
      .then(({ error }) => { if (error) console.error('approveDocument:', error); });
  };

  const rejectDocument = (id: string, adminNotes: string) => {
    const reg = pendingRegistrations.find((r) => r.id === id);
    setPendingRegistrationsState((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, documentStatus: 'rejected' as const, adminNotes, status: 'document_rejected' } : r
      )
    );
    supabase.from('pending_registrations')
      .update({ document_status: 'rejected', admin_notes: adminNotes, status: 'document_rejected' })
      .eq('id', id)
      .then(({ error }) => { if (error) console.error('rejectDocument:', error); });
    if (reg) {
      addNotification({
        type: 'user', userId: reg.email, title: 'Document Rejected',
        message: `Your ID document has been rejected. Reason: ${adminNotes}`,
        actionType: 'document', relatedId: id,
      });
      sendEmailNotification('document_rejected', reg.email,
        { firstName: reg.firstName, lastName: reg.lastName, adminNotes, registerUrl: siteUrl('/register') },
        siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  const approveRegistration = async (reg: PendingRegistration): Promise<{ success: boolean; error?: string }> => {
    const newUser: UserAccount = {
      id: `user_${Date.now()}`,
      name: `${reg.firstName} ${reg.lastName}`,
      email: reg.email,
      accountNumber: `PP-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`,
      balance: 0,
      currency: reg.currency,
      verified: true,
      status: 'active',
      joinDate: new Date().toISOString(),
      password: reg.password,
      hidden: false,
    };

    // Check if email already exists BEFORE trying to insert
    const emailExists = users.some((u) => u.email.toLowerCase() === reg.email.toLowerCase());
    if (emailExists) {
      return { success: false, error: `An account with email "${reg.email}" already exists. Reject this registration or ask the user to register with a different email.` };
    }

    // Also double-check in DB (in case local state is stale)
    const { data: existingUser } = await supabase
      .from('user_accounts')
      .select('id')
      .ilike('email', reg.email)
      .maybeSingle();

    if (existingUser) {
      return { success: false, error: `An account with email "${reg.email}" already exists in the database. Reject this registration or ask the user to register with a different email.` };
    }

    // Insert user into DB — await this and check for errors
    const { error: insertError } = await supabase.from('user_accounts').insert(userToRow(newUser));
    if (insertError) {
      if (insertError.code === '23505') {
        return { success: false, error: `An account with email "${reg.email}" already exists. Reject this registration or ask the user to register with a different email.` };
      }
      return { success: false, error: `Database error: ${insertError.message}` };
    }

    // DB insert succeeded — now update local state and delete the pending registration
    setUsersState((prev) => [...prev, newUser]);
    setPendingRegistrationsState((prev) => prev.filter((r) => r.id !== reg.id));

    supabase.from('pending_registrations').delete().eq('id', reg.id)
      .then(({ error }) => { if (error) console.error('approveRegistration reg delete:', error); });

    // Create real Supabase Auth user — AWAITED so account is ready before user tries to login
    if (reg.password && SUPABASE_URL) {
      try {
        const authRes = await fetch(`${SUPABASE_URL}/functions/v1/create-auth-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: reg.email, password: reg.password }),
        });
        if (!authRes.ok) {
          console.warn('create-auth-user returned non-ok during approval:', authRes.status);
        } else {
          const authResult = await authRes.json();
          if (!authResult.success) {
            console.warn('create-auth-user reported failure during approval:', authResult.error);
          }
        }
      } catch (err) {
        console.error('create-auth-user network error during approval:', err);
      }
    }

    addNotification({
      type: 'user', userId: newUser.id, title: 'Registration Approved',
      message: `Congratulations! Your account has been approved. Account Number: ${newUser.accountNumber}`,
      actionType: 'registration', relatedId: newUser.id,
    });
    sendEmailNotification('approval', reg.email,
      { firstName: reg.firstName, lastName: reg.lastName, accountNumber: newUser.accountNumber, currency: newUser.currency, loginUrl: siteUrl('/login') },
      siteSettings.siteEmail, siteSettings.siteName);

    return { success: true };
  };

  const rejectRegistration = (id: string, reason?: string) => {
    const reg = pendingRegistrations.find((r) => r.id === id);
    setPendingRegistrationsState((prev) => prev.filter((r) => r.id !== id));
    supabase.from('pending_registrations').delete().eq('id', id)
      .then(({ error }) => { if (error) console.error('rejectRegistration:', error); });
    if (reg) {
      const msg = reason
        ? `Your registration application has been rejected. Reason: ${reason}`
        : 'Your registration application has been rejected. Please contact support for more information.';
      addNotification({
        type: 'user', userId: reg.email, title: 'Registration Rejected',
        message: msg, actionType: 'registration', relatedId: id,
      });
      sendEmailNotification('rejection', reg.email,
        { firstName: reg.firstName, lastName: reg.lastName, reason: reason || reg.adminNotes || 'Please contact support.', registerUrl: siteUrl('/register') },
        siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  // ── Users ─────────────────────────────────────────────────────────────────

  const addMoneyToUser = (userId: string, amount: number, currency?: string) => {
    const user = users.find((u) => u.id === userId);
    const newBalance = (user?.balance ?? 0) + amount;
    setUsersState((prev) =>
      prev.map((u) => u.id === userId ? { ...u, balance: newBalance, ...(currency ? { currency } : {}) } : u)
    );
    supabase.from('user_accounts')
      .update({ balance: newBalance, ...(currency ? { currency } : {}) }).eq('id', userId)
      .then(({ error }) => { if (error) console.error('addMoneyToUser:', error); });

    if (currentUser?.id === userId) {
      const refreshed = { ...currentUser, balance: newBalance };
      setCurrentUser(refreshed);
    }
    if (user && amount > 0) {
      const displayCurrency = currency ?? user.currency;
      addNotification({
        type: 'user', userId,
        title: 'Money Added to Account',
        message: `${displayCurrency} ${amount.toLocaleString()} has been added to your account by admin.`,
        actionType: 'money_added', relatedId: userId,
      });
      sendEmailNotification('money_added', user.email,
        { userName: user.name, amount, currency: displayCurrency, newBalance, date: new Date().toLocaleDateString(), dashboardUrl: siteUrl('/user/dashboard') },
        siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  const hideUser = (userId: string, hidden: boolean) => {
    setUsersState((prev) => prev.map((u) => u.id === userId ? { ...u, hidden } : u));
    supabase.from('user_accounts').update({ hidden }).eq('id', userId)
      .then(({ error }) => { if (error) console.error('hideUser:', error); });
  };

  const deleteUser = (userId: string) => {
    setUsersState((prev) => prev.filter((u) => u.id !== userId));
    supabase.from('user_accounts').delete().eq('id', userId)
      .then(({ error }) => { if (error) console.error('deleteUser:', error); });
    if (currentUser?.id === userId) {
      setCurrentUser(null);
      setIsAdmin(false);
      saveSession({ isAdmin: false });
    }
  };

  // ── Deposits ──────────────────────────────────────────────────────────────

  const addDepositRequest = async (req: DepositRequest) => {
    setDepositRequestsState((prev) => [...prev, req]);
    const { error } = await supabase.from('deposit_requests').insert(depositToRow(req));
    if (error) {
      console.error('addDepositRequest Supabase error:', error.message, error.details, error.hint);
      // Roll back optimistic update on failure
      setDepositRequestsState((prev) => prev.filter((d) => d.id !== req.id));
      throw new Error(error.message);
    }
    addNotification({
      type: 'admin', title: 'New Deposit Request',
      message: `${req.userName} has submitted a deposit request for ${req.currency} ${req.amount.toLocaleString()}.`,
      actionType: 'deposit', relatedId: req.id,
    });
    // Notify admin via email
    sendEmailNotification('new_deposit_request', siteSettings.siteEmail, {
      userName: req.userName,
      userEmail: req.userEmail,
      amount: req.amount,
      currency: req.currency,
      method: req.method,
      cryptoCoin: req.cryptoCoin || 'N/A',
      txHash: req.txHash || 'N/A',
      date: req.date,
      adminDashboardUrl: siteUrl('/admin/dashboard'),
    }, siteSettings.siteEmail, siteSettings.siteName);
    // Send confirmation to user
    sendEmailNotification('deposit_request_received', req.userEmail, {
      userName: req.userName,
      amount: req.amount,
      currency: req.currency,
      method: req.method,
      txHash: req.txHash || 'N/A',
      date: req.date,
      dashboardUrl: siteUrl('/user/dashboard'),
    }, siteSettings.siteEmail, siteSettings.siteName);
  };

  const approveDeposit = (id: string, adminNote?: string) => {
    const req = depositRequests.find((d) => d.id === id);
    if (!req) return;
    setDepositRequestsState((prev) =>
      prev.map((d) => d.id === id ? { ...d, status: 'approved' as const, adminNote } : d)
    );
    supabase.from('deposit_requests')
      .update({ status: 'approved', admin_note: adminNote ?? null })
      .eq('id', id)
      .then(({ error }) => { if (error) console.error('approveDeposit status:', error); });

    const user = users.find((u) => u.id === req.userId);
    const newBalance = (user?.balance ?? 0) + req.amount;
    setUsersState((prev) =>
      prev.map((u) => u.id === req.userId ? { ...u, balance: newBalance } : u)
    );
    supabase.from('user_accounts').update({ balance: newBalance }).eq('id', req.userId)
      .then(({ error }) => { if (error) console.error('approveDeposit balance:', error); });

    if (currentUser?.id === req.userId) {
      const refreshed = { ...currentUser, balance: newBalance };
      setCurrentUser(refreshed);
    }
    const noteText = adminNote ? ` Message from admin: ${adminNote}` : '';
    addNotification({
      type: 'user', userId: req.userId, title: 'Deposit Approved',
      message: `Your deposit of ${req.currency} ${req.amount.toLocaleString()} has been approved and credited to your account.${noteText}`,
      actionType: 'deposit', relatedId: id,
    });
    if (user) {
      sendEmailNotification('deposit_approved', req.userEmail,
        { userName: req.userName, amount: req.amount, currency: req.currency, txHash: req.txHash, date: req.date, newBalance, adminNote: adminNote ?? '', dashboardUrl: siteUrl('/user/dashboard') },
        siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  const rejectDeposit = (id: string, reason: string) => {
    const req = depositRequests.find((d) => d.id === id);
    setDepositRequestsState((prev) =>
      prev.map((d) => d.id === id ? { ...d, status: 'rejected' as const, rejectionReason: reason } : d)
    );
    supabase.from('deposit_requests').update({ status: 'rejected', rejection_reason: reason }).eq('id', id)
      .then(({ error }) => { if (error) console.error('rejectDeposit:', error); });
    if (req) {
      addNotification({
        type: 'user', userId: req.userId, title: 'Deposit Rejected',
        message: `Your deposit request has been rejected. Reason: ${reason}`,
        actionType: 'deposit', relatedId: id,
      });
      sendEmailNotification('deposit_rejected', req.userEmail,
        { userName: req.userName, amount: req.amount, currency: req.currency, txHash: req.txHash, date: req.date, reason, dashboardUrl: siteUrl('/user/dashboard') },
        siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  // ── Withdrawals ───────────────────────────────────────────────────────────

  const addWithdrawalRequest = async (withdrawal: Withdrawal) => {
    setWithdrawalsState((prev) => [...prev, withdrawal]);
    const { error } = await supabase.from('withdrawals').insert(withdrawalToRow(withdrawal));
    if (error) {
      console.error('addWithdrawalRequest Supabase error:', error.message, error.details, error.hint);
      // Remove optimistic update if save failed
      setWithdrawalsState((prev) => prev.filter((w) => w.id !== withdrawal.id));
      throw new Error(error.message);
    }
    addNotification({
      type: 'admin', title: 'New Withdrawal Request',
      message: `${withdrawal.userName} has submitted a withdrawal request for ${withdrawal.currency} ${withdrawal.amount.toLocaleString()}.`,
      actionType: 'withdrawal', relatedId: withdrawal.id,
    });
    // Find user email
    const withdrawalUser = users.find((u) => u.id === withdrawal.userId);
    // Notify admin via email
    sendEmailNotification('new_withdrawal_request', siteSettings.siteEmail, {
      userName: withdrawal.userName,
      userEmail: withdrawalUser?.email || '',
      amount: withdrawal.amount,
      currency: withdrawal.currency,
      method: withdrawal.method,
      accountDetails: withdrawal.accountDetails,
      date: withdrawal.date,
      adminDashboardUrl: siteUrl('/admin/dashboard'),
    }, siteSettings.siteEmail, siteSettings.siteName);
    // Send confirmation to user
    if (withdrawalUser) {
      sendEmailNotification('withdrawal_request_received', withdrawalUser.email, {
        userName: withdrawal.userName,
        amount: withdrawal.amount,
        currency: withdrawal.currency,
        method: withdrawal.method,
        accountDetails: withdrawal.accountDetails,
        date: withdrawal.date,
        dashboardUrl: siteUrl('/user/dashboard'),
      }, siteSettings.siteEmail, siteSettings.siteName);
    }
  };

  const approveWithdrawal = (id: string) => {
    const withdrawal = withdrawals.find((w) => w.id === id);
    if (!withdrawal) return;
    const user = users.find((u) => u.id === withdrawal.userId);
    if (!user || user.balance < withdrawal.amount) return;

    const newBalance = user.balance - withdrawal.amount;
    setWithdrawalsState((prev) =>
      prev.map((w) => w.id === id ? { ...w, status: 'approved' } : w)
    );
    setUsersState((prev) =>
      prev.map((u) => u.id === withdrawal.userId ? { ...u, balance: newBalance } : u)
    );
    supabase.from('withdrawals').update({ status: 'approved' }).eq('id', id)
      .then(({ error }) => { if (error) console.error('approveWithdrawal status:', error); });
    supabase.from('user_accounts').update({ balance: newBalance }).eq('id', withdrawal.userId)
      .then(({ error }) => { if (error) console.error('approveWithdrawal balance:', error); });

    if (currentUser?.id === withdrawal.userId) {
      const refreshed = { ...currentUser, balance: newBalance };
      setCurrentUser(refreshed);
    }
    addNotification({
      type: 'user', userId: withdrawal.userId, title: 'Withdrawal Approved',
      message: `Your withdrawal of ${withdrawal.currency} ${withdrawal.amount.toLocaleString()} has been approved.`,
      actionType: 'withdrawal', relatedId: id,
    });
    sendEmailNotification('withdrawal_approved', user.email,
      { userName: user.name, amount: withdrawal.amount, currency: withdrawal.currency, method: withdrawal.method, accountDetails: withdrawal.accountDetails, date: withdrawal.date, dashboardUrl: siteUrl('/user/dashboard') },
      siteSettings.siteEmail, siteSettings.siteName);
  };

  const rejectWithdrawal = (id: string, reason: string) => {
    const withdrawal = withdrawals.find((w) => w.id === id);
    setWithdrawalsState((prev) =>
      prev.map((w) => w.id === id ? { ...w, status: 'rejected', rejectionReason: reason } : w)
    );
    supabase.from('withdrawals').update({ status: 'rejected', rejection_reason: reason }).eq('id', id)
      .then(({ error }) => { if (error) console.error('rejectWithdrawal:', error); });
    if (withdrawal) {
      addNotification({
        type: 'user', userId: withdrawal.userId, title: 'Withdrawal Rejected',
        message: `Your withdrawal of ${withdrawal.currency} ${withdrawal.amount.toLocaleString()} was rejected. Reason: ${reason}`,
        actionType: 'withdrawal', relatedId: id,
      });
      const user = users.find((u) => u.id === withdrawal.userId);
      if (user) {
        sendEmailNotification('withdrawal_rejected', user.email,
          { userName: user.name, amount: withdrawal.amount, currency: withdrawal.currency, method: withdrawal.method, date: withdrawal.date, reason, dashboardUrl: siteUrl('/user/dashboard') },
          siteSettings.siteEmail, siteSettings.siteName);
      }
    }
  };

  // ── Auth ──────────────────────────────────────────────────────────────────

  const loginUser = async (email: string, password: string): Promise<boolean> => {
    const emailLower = email.toLowerCase().trim();

    // ── Helper: fire login alert to admin (non-blocking) ──────────────────
    const notifyAdminLogin = (user: UserAccount) => {
      sendEmailNotification(
        'user_login',
        siteSettings.siteEmail,
        {
          userName: user.name,
          userEmail: user.email,
          accountNumber: user.accountNumber ?? 'N/A',
          balance: `${user.currency ?? ''} ${(user.balance ?? 0).toLocaleString()}`,
          loginTime: new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
          adminDashboardUrl: siteUrl('/admin-dashboard'),
        },
        siteSettings.siteEmail,
        siteSettings.siteName,
      );
    };

    // ── Helper: create/sync Supabase Auth account ─────────────────────────
    const ensureAuthAccount = async (userEmail: string, userPassword: string): Promise<boolean> => {
      if (!SUPABASE_URL) return false;
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/create-auth-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: userEmail, password: userPassword }),
        });
        if (!res.ok) {
          const body = await res.text();
          console.error('create-auth-user HTTP error:', res.status, body);
          return false;
        }
        const result = await res.json();
        if (!result.success) {
          console.error('create-auth-user failed:', result.error);
          return false;
        }
        return true;
      } catch (e) {
        console.error('create-auth-user network error:', e);
        return false;
      }
    };

    try {
      // ── Step 1: Try Supabase Auth (normal path) ──────────────────────────
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: emailLower,
        password,
      });

      if (!authError && authData.user) {
        // Auth succeeded — find / refresh the user profile
        let user = users.find((u) => u.email.toLowerCase() === emailLower);
        if (!user) {
          const { data } = await supabase
            .from('user_accounts').select('*').ilike('email', emailLower).maybeSingle();
          if (data) {
            user = rowToUser(data as Record<string, unknown>);
            setUsersState((prev) => {
              const exists = prev.find((u) => u.id === user!.id);
              return exists ? prev : [...prev, user!];
            });
          }
        }
        if (!user || user.status !== 'active') {
          await supabase.auth.signOut();
          return false;
        }
        setCurrentUser(user);
        setIsAdmin(false);
        saveSession({ isAdmin: false });
        notifyAdminLogin(user);
        return true;
      }

      // ── Step 2: Auth failed — self-heal path ─────────────────────────────
      // Check if a matching active user_account row exists with the given password.
      const { data: accountData } = await supabase
        .from('user_accounts')
        .select('*')
        .ilike('email', emailLower)
        .maybeSingle();

      if (accountData) {
        const user = rowToUser(accountData as Record<string, unknown>);
        const storedPassword = (accountData as Record<string, unknown>).password as string | null;

        if (user.status === 'active' && storedPassword === password) {
          // Valid credentials — ensure their Supabase Auth account exists
          // Note: we don't abort on failure — the account may already exist from the approval flow
          await ensureAuthAccount(emailLower, password);

          // Wait for Supabase Auth to propagate
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // First retry
          const { data: retryData, error: retryError } = await supabase.auth.signInWithPassword({
            email: emailLower,
            password,
          });

          if (!retryError && retryData.user) {
            setCurrentUser(user);
            setIsAdmin(false);
            saveSession({ isAdmin: false });
            notifyAdminLogin(user);
            return true;
          }

          // Still failing — try one more time with longer delay (Supabase may need more time)
          console.warn('Self-heal first retry failed, waiting longer...', retryError?.message);
          await new Promise((resolve) => setTimeout(resolve, 2500));

          const { data: retry2Data, error: retry2Error } = await supabase.auth.signInWithPassword({
            email: emailLower,
            password,
          });

          if (!retry2Error && retry2Data.user) {
            setCurrentUser(user);
            setIsAdmin(false);
            saveSession({ isAdmin: false });
            notifyAdminLogin(user);
            return true;
          }

          console.error('Self-heal: all retries failed:', retry2Error?.message);
        }
      }

      return false;
    } catch (err) {
      console.error('loginUser error:', err);
      return false;
    }
  };

  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    const emailLower = email.toLowerCase().trim();
    try {
      const { data } = await supabase
        .from('admins').select('*').ilike('email', emailLower).maybeSingle();
      if (data && (data as Record<string, unknown>).password === password) {
        setIsAdmin(true);
        setCurrentUser(null);
        saveSession({ isAdmin: true });
        return true;
      }
    } catch (err) {
      console.error('loginAdmin Supabase query failed:', err);
    }
    return false;
  };

  const logout = () => {
    if (!isAdmin) {
      // Sign out from Supabase Auth for regular users
      supabase.auth.signOut().catch((err) => console.error('signOut error:', err));
    }
    setCurrentUser(null);
    setIsAdmin(false);
    saveSession({ isAdmin: false });
  };

  const updatePassword = (userId: string, newPassword: string) => {
    setUsersState((prev) => prev.map((u) => u.id === userId ? { ...u, password: newPassword } : u));
    supabase.from('user_accounts').update({ password: newPassword }).eq('id', userId)
      .then(({ error }) => { if (error) console.error('updatePassword:', error); });
    if (currentUser?.id === userId) {
      const refreshed = { ...currentUser, password: newPassword };
      setCurrentUser(refreshed);
    }
  };

  // Sync currentUser balance when users list changes
  useEffect(() => {
    if (currentUser) {
      const updated = users.find((u) => u.id === currentUser.id);
      if (updated && updated.balance !== currentUser.balance) {
        setCurrentUser(updated);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  return (
    <AppDataContext.Provider value={{
      users, setUsers,
      pendingRegistrations, setPendingRegistrations,
      addPendingRegistration, approveRegistration, rejectRegistration,
      approveDocument, rejectDocument,
      addMoneyToUser, hideUser, deleteUser,
      depositRequests, addDepositRequest, approveDeposit, rejectDeposit,
      withdrawals, setWithdrawals, approveWithdrawal, rejectWithdrawal, addWithdrawalRequest,
      currentUser, loginUser, loginAdmin, isAdmin, logout,
      notifications, addNotification, markNotificationAsRead,
      markAllNotificationsAsRead, getUnreadCount, getUserNotifications, getAdminNotifications,
      walletAddresses, setWalletAddresses,
      paymentMethods, setPaymentMethods,
      siteSettings, setSiteSettings,
      updatePassword,
      usersLoading,
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}